import { useState, useCallback, useRef } from 'react';
import * as AlertDialog from '@radix-ui/react-alert-dialog';
import { CheckCircleIcon, DocumentTextIcon } from '@heroicons/react/solid';
import { XIcon } from '@heroicons/react/outline';
import { apiHfpTestSimulateCounterpart, apiHfpTestGenerateReport } from '../../utils/hfpTestApi';

/**
 * HfpTestGenerateProfileModal
 * ---------------------------
 * Staging-only dialog for the HFP test site. In the real RMS the profile only
 * appears once BOTH the parent/carer and the school have answered; on the test
 * site there is a single tester, so this dialog explains (in plain terms) that
 * the other set of answers will be filled in for them, lets them pick how
 * closely the two views should agree, then generates the answers and the
 * profile with a step-by-step progress view. Styled to match
 * RmsAllFormsCompletedModal (same overlay, card, icon header and gradient CTA).
 *
 * Props:
 *   - isOpen: whether the dialog is visible
 *   - onClose: close the dialog (disabled while generating)
 *   - onView: navigate to the profile page (done stage CTA)
 *   - onGenerated: notify the parent that data changed (refresh the overview)
 *   - childName: CYP first name for personalised copy
 *   - roleSuffix: the tester's role ('guardian' | 'school_user')
 *   - patientRecordId: the tester's demo patient record
 */

// Service-user friendly agreement choices. Ids map to the backend presets
// (_AGREEMENT_PRESETS in hfp_test_site.py); "medium" is the moderate preset.
const AGREEMENT_CHOICES = [
  { id: 'high', label: 'High agreement', description: 'Home and school see mostly the same things.' },
  { id: 'moderate', label: 'Medium agreement', description: 'Some things look different at home than at school.' },
  { id: 'low', label: 'Low agreement', description: 'Home and school see quite different things.' },
];

function Spinner() {
  return (
    <svg className="animate-spin w-5 h-5 text-primary" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
    </svg>
  );
}

function ProgressStep({ state, label }) {
  // state: 'pending' | 'active' | 'done'
  return (
    <li className="flex items-center gap-3">
      <span className="flex-shrink-0 w-7 h-7 flex items-center justify-center">
        {state === 'done' ? (
          <CheckCircleIcon className="w-7 h-7 text-green-500" aria-hidden="true" />
        ) : state === 'active' ? (
          <Spinner />
        ) : (
          <span className="w-3 h-3 rounded-full bg-gray-300" aria-hidden="true" />
        )}
      </span>
      <span className={`text-sm ${state === 'pending' ? 'text-gray-400' : 'text-gray-800'}`}>{label}</span>
    </li>
  );
}

export default function HfpTestGenerateProfileModal({
  isOpen,
  onClose,
  onView,
  onGenerated,
  childName,
  roleSuffix,
  patientRecordId,
}) {
  // stage: 'choose' | 'working' | 'done' | 'error'
  const [stage, setStage] = useState('choose');
  const [agreement, setAgreement] = useState('high');
  // 'answers' | 'profile' — which generation step is currently running.
  const [workingStep, setWorkingStep] = useState('answers');
  const [errorMessage, setErrorMessage] = useState(null);
  // Remembers a completed answers step so "Try again" only re-runs what failed.
  const answersDoneRef = useRef(false);

  const roleLabel = roleSuffix === 'guardian' ? 'parent / carer' : 'school';
  const otherRoleLabel = roleSuffix === 'guardian' ? 'school' : 'parent / carer';
  const name = (childName || '').trim() || 'the child';

  const runGeneration = useCallback(async () => {
    setStage('working');
    setErrorMessage(null);
    try {
      if (!answersDoneRef.current) {
        setWorkingStep('answers');
        await apiHfpTestSimulateCounterpart({ patientRecordId, roleSuffix, agreement });
        answersDoneRef.current = true;
      }
      setWorkingStep('profile');
      if (onGenerated) onGenerated();
      const res = await apiHfpTestGenerateReport({ patientRecordId });
      if (!res?.report) throw new Error('The profile could not be created. Please try again.');
      setStage('done');
      if (onGenerated) onGenerated();
    } catch (err) {
      setErrorMessage(err?.message || 'Something went wrong. Please try again.');
      setStage('error');
    }
  }, [patientRecordId, roleSuffix, agreement, onGenerated]);

  const handleClose = useCallback(() => {
    if (stage === 'working') return; // keep the dialog up while generating
    setStage('choose');
    setErrorMessage(null);
    answersDoneRef.current = false;
    onClose();
  }, [stage, onClose]);

  const renderChoose = () => (
    <>
      <div className="text-center mb-5">
        <div className="flex justify-center mb-3">
          <div className="w-14 h-14 bg-primary/10 rounded-full flex items-center justify-center">
            <DocumentTextIcon className="w-9 h-9 text-primary" aria-hidden="true" />
          </div>
        </div>
        <AlertDialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
          Generate {name}&apos;s profile
        </AlertDialog.Title>
        <AlertDialog.Description className="text-gray-600 text-sm sm:text-base leading-relaxed text-left">
          Usually, a parent or carer and someone from school both answer these questions
          about the same child. The profile brings the two views together to show {name}&apos;s
          strengths and support needs at home and at school.
        </AlertDialog.Description>
        <AlertDialog.Description className="text-gray-600 text-sm sm:text-base leading-relaxed text-left mt-2">
          You have filled in the {roleLabel} answers. So you can see the finished profile,
          we will fill in the {otherRoleLabel} answers for you, based on yours.
        </AlertDialog.Description>
      </div>

      <fieldset className="mb-6">
        <legend className="text-sm font-semibold text-gray-900 mb-2">
          How closely should the two views agree?
        </legend>
        <div className="space-y-2">
          {AGREEMENT_CHOICES.map((choice) => {
            const selected = agreement === choice.id;
            return (
              <label
                key={choice.id}
                className={`flex items-start gap-3 rounded-xl border-2 p-3 cursor-pointer transition-colors ${
                  selected ? 'border-primary bg-primary/5' : 'border-gray-200 bg-white hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="hfp-test-agreement"
                  value={choice.id}
                  checked={selected}
                  onChange={() => setAgreement(choice.id)}
                  className="mt-0.5 w-4 h-4 text-primary focus:ring-primary"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-semibold text-gray-900">{choice.label}</span>
                  <span className="block text-sm text-gray-600">{choice.description}</span>
                </span>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="w-full sm:w-1/3 py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          onClick={runGeneration}
          className="w-full sm:flex-1 py-3 px-4 bg-gradient-to-r from-primary to-teal-600 text-white rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
        >
          <DocumentTextIcon className="w-5 h-5" aria-hidden="true" />
          Generate profile
        </button>
      </div>
    </>
  );

  const renderWorking = () => (
    <>
      <div className="text-center mb-5">
        <AlertDialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-2 leading-tight">
          Creating {name}&apos;s profile
        </AlertDialog.Title>
        <AlertDialog.Description className="text-gray-600 text-sm sm:text-base leading-relaxed">
          This can take a minute or two — please keep this page open.
        </AlertDialog.Description>
      </div>
      <ol className="bg-gray-50 border border-gray-200 rounded-xl p-4 space-y-3">
        <ProgressStep
          state={workingStep === 'answers' ? 'active' : 'done'}
          label={`Filling in the ${otherRoleLabel} answers`}
        />
        <ProgressStep
          state={workingStep === 'profile' ? 'active' : 'pending'}
          label="Putting the profile together"
        />
      </ol>
    </>
  );

  const renderDone = () => (
    <>
      <div className="text-center mb-6">
        <div className="flex justify-center mb-3">
          <div className="w-14 h-14 bg-green-100 rounded-full flex items-center justify-center">
            <CheckCircleIcon className="w-9 h-9 text-green-500" aria-hidden="true" />
          </div>
        </div>
        <AlertDialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
          {name}&apos;s profile is ready
        </AlertDialog.Title>
        <AlertDialog.Description className="text-gray-600 text-sm sm:text-base leading-relaxed">
          An overview of {name}&apos;s strengths and support needs at home and at school.
        </AlertDialog.Description>
      </div>
      <button
        type="button"
        onClick={() => {
          handleClose();
          onView();
        }}
        className="w-full py-3 px-4 bg-gradient-to-r from-primary to-teal-600 text-white rounded-lg font-medium hover:from-teal-700 hover:to-teal-800 transition-all duration-200 ease-out transform hover:scale-[1.02] active:scale-[0.98] shadow-md hover:shadow-lg flex items-center justify-center gap-2"
      >
        <DocumentTextIcon className="w-5 h-5" aria-hidden="true" />
        View Holistic Functional Profile
      </button>
    </>
  );

  const renderError = () => (
    <>
      <div className="text-center mb-5">
        <AlertDialog.Title className="text-xl sm:text-2xl font-bold text-gray-900 mb-3 leading-tight">
          Something went wrong
        </AlertDialog.Title>
        <AlertDialog.Description className="text-gray-600 text-sm sm:text-base leading-relaxed">
          We could not finish creating the profile. Nothing has been lost — please try again.
        </AlertDialog.Description>
      </div>
      {errorMessage && (
        <div className="mb-5 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 text-left">
          {errorMessage}
        </div>
      )}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          type="button"
          onClick={handleClose}
          className="w-full sm:w-1/3 py-3 px-4 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-colors"
        >
          Close
        </button>
        <button
          type="button"
          onClick={runGeneration}
          className="w-full sm:flex-1 py-3 px-4 bg-primary text-white rounded-lg font-medium hover:bg-primary/90 transition-colors"
        >
          Try again
        </button>
      </div>
    </>
  );

  return (
    <AlertDialog.Root open={isOpen}>
      <AlertDialog.Portal>
        <AlertDialog.Overlay className="radix-dialog-overlay-motion fixed inset-0 bg-black/60 z-50 backdrop-blur-sm" />
        <AlertDialog.Content className="radix-dialog-content-centered-motion fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 bg-white rounded-2xl shadow-2xl p-6 sm:p-8 w-[95vw] sm:w-[90vw] max-w-lg max-h-[90vh] overflow-y-auto z-50">
          <div className="relative">
            {stage === 'choose' && renderChoose()}
            {stage === 'working' && renderWorking()}
            {stage === 'done' && renderDone()}
            {stage === 'error' && renderError()}
          </div>

          {stage !== 'working' && (
            <AlertDialog.Cancel asChild>
              <button
                onClick={handleClose}
                className="absolute top-4 right-4 sm:top-6 sm:right-6 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-all"
                aria-label="Close"
              >
                <XIcon className="w-5 h-5" />
              </button>
            </AlertDialog.Cancel>
          )}
        </AlertDialog.Content>
      </AlertDialog.Portal>
    </AlertDialog.Root>
  );
}
